/**
 * Bulk Send wizard (all channels). Flow:
 *  1. Template — pick the template first (so we know the required variables),
 *     configure media/buttons (WhatsApp), and download a sample CSV/Excel whose
 *     columns match the template's required fields.
 *  2. Contacts — upload CSV/Excel (or paste), then MAP each file column to the
 *     recipient and to each template variable.
 *  3. Review & send.
 *
 * Page is two columns: content on the left, a live preview on the right.
 */

import { useEffect, useMemo, useState } from 'react';
import * as XLSX from 'xlsx';
import { IconCircleCheck, IconDownload, IconPaperclip, IconX } from '@tabler/icons-react';
import { FileButton } from '@mantine/core';
import { EmailBodyEditor } from './EmailBodyEditor';
import { HtmlEmailPreview } from './HtmlEmailPreview';
import { useFileUpload } from '../../../../components/ccms-components/file-management/hooks/useFileUpload';
import type { EmailAttachmentRef } from '../Email/types';
import { CompactWizard } from './CompactWizard';
import { Stack, Group, Text, Title, Badge, TextInput, Textarea, Button, Select, Switch, Modal, Alert, SegmentedControl, Table, SimpleGrid, Grid, DateTimePicker, ActionIcon } from '../../../../components/ui';
import { Card } from '../../../../components/ui/data-display';
import { toast } from '../../../../components/ui/providers/AlertProvider';
import { useCampaigns, type CampaignView, type CampaignRecipientInput } from './useCampaigns';
import { useApplicationCatalogueService, type AppManifest } from '@actschurch/ccms-sdk';
import { PushImageField, PushSoundField } from './PushMediaFields';
import { parsePushSpec } from '../PushNotification/types';
import { useChannelTemplates } from './useChannelTemplates';
import { CHANNEL_IDS } from './useCommunicationSettings';
import { WhatsAppTemplateComposer, type ComposerOutput } from './WhatsAppTemplateComposer';
import { WhatsAppMessagePreview } from './WhatsAppMessagePreview';
import { maxPlaceholder } from './whatsappTemplate';
import { toInternational } from './phone';
import { decodeMessageBody } from './decodeBody';
import { normalizeEmailHtml } from './emailHtml';

type Channel = 'email' | 'sms' | 'whatsapp' | 'push';

function parsePasteRows(text: string, flat: boolean): string[][] {
  if (flat) {
    // No template variables → a flat recipient list: commas / newlines / semicolons all
    // separate recipients (each becomes a single-column row).
    return text.split(/[\n,;]+/).map((s) => s.trim()).filter(Boolean).map((s) => [s]);
  }
  // Has variables → CSV-like: rows by newline, columns by comma (recipient + variables).
  return text.split(/\r?\n/).map((l) => l.trim()).filter(Boolean).map((l) => l.split(',').map((s) => s.trim()));
}

// Convert a picked local Date to a yyyyMMddHHmmss number in UTC (matches the
// backend's UtcNow comparison in the campaign scheduler).
function toUtcStamp(d: Date): number {
  const p = (n: number) => String(n).padStart(2, '0');
  return Number(`${d.getUTCFullYear()}${p(d.getUTCMonth() + 1)}${p(d.getUTCDate())}${p(d.getUTCHours())}${p(d.getUTCMinutes())}${p(d.getUTCSeconds())}`);
}

function subst(text: string, values?: string[]): string {
  if (!text || !values?.length) return text;
  let out = text;
  values.forEach((v, i) => { if (v) out = out.replace(new RegExp(`\\{\\{\\s*${i + 1}\\s*\\}\\}`, 'g'), v); });
  return out;
}

const digitsOnly = (s: string) => (s || '').replace(/\D/g, '');

/** Channel-appropriate validity check for a recipient address. */
function isValidRecipient(channel: Channel, addr: string): boolean {
  const a = (addr || '').trim();
  if (!a) return false;
  if (channel === 'email') return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a);
  if (channel === 'push') return a.length >= 6; // device tokens vary in shape
  const d = digitsOnly(a);                       // sms / whatsapp phone numbers
  return d.length >= 9 && d.length <= 15;
}

/** Dedupe key — phones collapse across formats (0xx / +27 / 27 / bare). */
function recipientKey(channel: Channel, addr: string): string {
  const a = (addr || '').trim();
  if (channel === 'email') return a.toLowerCase();
  if (channel === 'push') return a;
  let d = digitsOnly(a);
  if (d.startsWith('27') && d.length > 2) d = d.slice(2);
  else if (d.startsWith('0') && d.length > 1) d = d.slice(1);
  return d;
}

export function BulkSendWizard({ channel }: { channel: Channel }) {
  const channelId = CHANNEL_IDS[channel];
  const showSubject = channel === 'email' || channel === 'push';
  const isWhatsApp = channel === 'whatsapp';
  const isEmail = channel === 'email';
  const recipientLabel = channel === 'email' ? 'email' : channel === 'push' ? 'device_token' : 'phone';

  const { submitting, createAndSend, createCampaign } = useCampaigns();
  const { loading: tplLoading, templates, fetchTemplates } = useChannelTemplates();
  const catalogueService = useApplicationCatalogueService();

  // Push only: which app the campaign targets. The appId selects the APNs topic (bundle id) for every
  // push the campaign creates — without it iOS rejects them all with DeviceTokenNotForTopic.
  const isPush = channel === 'push';
  const [appId, setAppId] = useState<string | null>(null);
  const [imageUrl, setImageUrl] = useState('');
  const [sound, setSound] = useState('default');
  const [catalogue, setCatalogue] = useState<AppManifest[]>([]);

  useEffect(() => {
    if (!isPush) return;
    let active = true;
    (async () => {
      try {
        // /catalogue/all is admin-only; fall back to the entitlement-filtered catalogue if refused.
        let apps: AppManifest[] = [];
        try {
          apps = await catalogueService.getAllApps();
        } catch {
          apps = await catalogueService.getCatalogue();
        }
        if (active) setCatalogue(apps || []);
      } catch {
        if (active) setCatalogue([]);
      }
    })();
    return () => { active = false; };
  }, [isPush, catalogueService]);

  // Step 1 — template/content
  const [name, setName] = useState('');
  const [templateId, setTemplateId] = useState<string | null>(null);
  const [subject, setSubject] = useState('');
  const [body, setBody] = useState('');
  const [waOut, setWaOut] = useState<ComposerOutput | null>(null);
  const [attachments, setAttachments] = useState<EmailAttachmentRef[]>([]);
  const { upload: uploadAttachment, uploading: attUploading } = useFileUpload({
    domain: 'Communications',
    recordId: 'email-attachments',
    maxSize: 25 * 1024 * 1024,
    onComplete: (files) => {
      setAttachments((prev) => [
        ...prev,
        ...files.map((f) => ({ fileId: f.id, fileName: f.fileName, contentType: f.mimeType, size: f.fileSize })),
      ]);
      if (files.length) toast.success(`${files.length} attachment(s) uploaded`);
    },
    onError: (e) => toast.error(e?.message || 'Attachment upload failed'),
  });

  // Step 2 — contacts + mapping
  const [mode, setMode] = useState<'upload' | 'paste'>('upload');
  const [pasteText, setPasteText] = useState('');
  const [rows, setRows] = useState<string[][]>([]);
  const [fileName, setFileName] = useState('');
  const [hasHeader, setHasHeader] = useState(true);
  const [recipientCol, setRecipientCol] = useState(0);
  const [varCols, setVarCols] = useState<number[]>([]);

  const [includeOptedOut, setIncludeOptedOut] = useState(false);
  const [scheduleAt, setScheduleAt] = useState<Date | null>(null);
  const [result, setResult] = useState<CampaignView | null>(null);
  const [formKey, setFormKey] = useState(0);
  const [stepErrors, setStepErrors] = useState<string[]>([]);

  useEffect(() => { if (!isWhatsApp) fetchTemplates(channel); }, [channel, isWhatsApp, fetchTemplates]);

  // Required variables come from the chosen template.
  const requiredVarCount = isWhatsApp
    ? (waOut?.structure?.bodyVarCount ?? 0)
    : maxPlaceholder(`${subject}\n${body}`);

  // Keep the var→column mapping sized to the template's variable count.
  useEffect(() => {
    setVarCols((prev) => Array.from({ length: requiredVarCount }, (_, i) => prev[i] ?? i + 1));
  }, [requiredVarCount]);

  const dataRows = useMemo(
    () => (mode === 'paste' ? parsePasteRows(pasteText, requiredVarCount === 0) : hasHeader ? rows.slice(1) : rows),
    [mode, pasteText, rows, hasHeader, requiredVarCount],
  );
  const width = useMemo(() => {
    const w = Math.max(0, ...dataRows.map((r) => r.length));
    return mode === 'upload' && hasHeader ? Math.max(w, rows[0]?.length ?? 0) : w;
  }, [dataRows, mode, hasHeader, rows]);
  const columns = useMemo<string[]>(() => {
    if (mode === 'upload' && hasHeader && rows[0]) return rows[0].map((c, i) => String(c || `Column ${i + 1}`));
    return Array.from({ length: width }, (_, i) => `Column ${i + 1}`);
  }, [mode, hasHeader, rows, width]);

  const isPhoneChannel = channel === 'sms' || channel === 'whatsapp';
  const recipients: CampaignRecipientInput[] = useMemo(
    () =>
      dataRows
        .map((r) => {
          const raw = String(r[recipientCol] ?? '').trim();
          // Phone channels: accept local (0…) numbers and normalize to international (+27…).
          return {
            toAddress: isPhoneChannel ? toInternational(raw) : raw,
            variables: varCols.map((ci) => String(r[ci] ?? '').trim()),
          };
        })
        .filter((r) => r.toAddress.length > 0),
    [dataRows, recipientCol, varCols, isPhoneChannel],
  );

  // Validate + dedupe → only valid, unique recipients are actually sent.
  const { valid, invalidCount, dupCount, invalidSamples } = useMemo(() => {
    const seen = new Set<string>();
    const valid: CampaignRecipientInput[] = [];
    const invalidSamples: string[] = [];
    let invalidCount = 0;
    let dupCount = 0;
    for (const r of recipients) {
      if (!isValidRecipient(channel, r.toAddress)) {
        invalidCount++;
        if (invalidSamples.length < 8) invalidSamples.push(r.toAddress);
        continue;
      }
      const key = recipientKey(channel, r.toAddress);
      if (seen.has(key)) { dupCount++; continue; }
      seen.add(key);
      valid.push(r);
    }
    return { valid, invalidCount, dupCount, invalidSamples };
  }, [recipients, channel]);

  const colOptions = columns.map((c, i) => ({ value: String(i), label: c }));
  const sampleVars = valid[0]?.variables;

  const handleFile = async (file: File | null) => {
    if (!file) return;
    setFileName(file.name);
    const buf = await file.arrayBuffer();
    const wb = XLSX.read(buf, { type: 'array' });
    const ws = wb.Sheets[wb.SheetNames[0]];
    setRows(XLSX.utils.sheet_to_json<string[]>(ws, { header: 1, blankrows: false, defval: '' }) as string[][]);
  };

  const onSelectTemplate = (id: string | null) => {
    setTemplateId(id);
    if (!isWhatsApp && id) {
      const t = templates.find((x) => x.id === id);
      // Template bodies are stored base64-encoded; decode (no-op on plain text) before editing.
      if (t) {
        const decoded = decodeMessageBody(t.body);

        // A push template is a notification SPEC (title/body/icon/data/actions), not a body. Putting
        // the raw JSON in the message field made the campaign send that JSON as the notification text.
        const spec = isPush ? parsePushSpec(decoded) : null;
        if (spec) {
          if (spec.title) setSubject(spec.title);   // "Subject" is the push Title for this channel
          setBody(spec.body ?? '');
        } else {
          setBody(decoded);
        }
      }

      // A push template that names its mobile app already answers "which app?" — that app selects the
      // APNs topic, so pre-select it rather than making the operator repeat it (and risk mismatching).
      if (isPush && t?.mobileAppId) setAppId(t.mobileAppId);
    }
  };

  const downloadSample = (ext: 'csv' | 'xlsx') => {
    const headers = [recipientLabel, ...Array.from({ length: requiredVarCount }, (_, i) => `variable_${i + 1}`)];
    const example = [channel === 'email' ? 'jane@example.com' : channel === 'push' ? 'ExponentPushToken[xxx]' : '+27821234567',
      ...Array.from({ length: requiredVarCount }, (_, i) => `value ${i + 1}`)];
    const ws = XLSX.utils.aoa_to_sheet([headers, example]);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Recipients');
    XLSX.writeFile(wb, `bulk_${channel}_recipients.${ext}`);
  };

  const reset = () => {
    setResult(null); setName(''); setTemplateId(null); setSubject(''); setBody(''); setWaOut(null);
    setPasteText(''); setRows([]); setFileName(''); setRecipientCol(0); setVarCols([]); setIncludeOptedOut(false);
    setScheduleAt(null); setStepErrors([]); setAttachments([]); setAppId(null); setImageUrl(''); setSound('default'); setFormKey((k) => k + 1);
  };

  // Computed when "Next" is pressed (via each step's validate) — lists what's missing.
  const errAlert =
    stepErrors.length > 0 ? (
      <Alert color="red" title="Please complete the following">
        <ul style={{ margin: 0, paddingLeft: 18 }}>
          {stepErrors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      </Alert>
    ) : null;

  const validateTemplateStep = () => {
    const m: string[] = [];
    if (!name.trim()) m.push('Campaign name');
    if (isWhatsApp) {
      if (!waOut?.template) m.push('Select an approved template');
      else m.push(...(waOut.errors ?? []));
    } else if (!body.trim()) {
      m.push('Message body');
    }
    // A push with no app goes out on the DEFAULT bundle id and iOS rejects it — don't let a whole
    // campaign be queued that way.
    if (isPush && !appId) m.push('Target app');
    setStepErrors(m);
    return m.length === 0;
  };

  const validateContactsStep = () => {
    const m: string[] = [];
    if (valid.length === 0) m.push('At least one valid recipient — upload or paste contacts, then map the recipient column');
    setStepErrors(m);
    return m.length === 0;
  };

  const wizard = (
    <CompactWizard
      key={formKey}
      completeLabel="Create & Send"
      loading={submitting}
      onComplete={async () => {
        const waStruct = waOut?.structure;
        const payload = {
          name: name.trim(),
          channelId,
          subject: showSubject ? subject : undefined,
          messageBody: isWhatsApp ? '' : (isEmail ? normalizeEmailHtml(body) : body),
          templateName: isWhatsApp ? waOut?.templateName : undefined,
          templateLanguage: isWhatsApp ? waOut?.templateLanguage ?? 'en' : undefined,
          templateId: !isWhatsApp && templateId ? Number(templateId) || undefined : undefined,
          headerMediaUrl: isWhatsApp && waStruct?.requiresMedia ? waOut?.mediaUrl : undefined,
          headerMediaType: isWhatsApp && waStruct?.requiresMedia ? waStruct.headerFormat : undefined,
          buttonUrlParam: isWhatsApp && (waStruct?.dynamicUrlButtonIndex ?? -1) >= 0 ? waOut?.buttonUrlParam : undefined,
          buttonIndex: isWhatsApp && (waStruct?.dynamicUrlButtonIndex ?? -1) >= 0 ? waStruct!.dynamicUrlButtonIndex : undefined,
          includeOptedOut,
          scheduledDateTime: scheduleAt ? toUtcStamp(scheduleAt) : undefined,
          attachmentsJson: isEmail && attachments.length > 0 ? JSON.stringify(attachments) : undefined,
          appId: isPush ? appId ?? undefined : undefined,
          imageUrl: isPush && imageUrl ? imageUrl : undefined,
          sound: isPush ? sound : undefined,
          recipients: valid,
        };
        try {
          // Scheduled → create only (the scheduler sends it later). Otherwise send now.
          const res = scheduleAt ? await createCampaign(payload) : await createAndSend(payload);
          if (res) setResult(res);
          else toast.error('Failed to create campaign');
        } catch (e: any) {
          toast.error(e?.message || 'Failed to create campaign');
        }
      }}
      steps={[
        {
          title: 'Template',
          description: 'Pick the template first so we know which fields the file needs.',
          validate: validateTemplateStep,
          content: (
            <Stack gap="sm">
              {errAlert}
              <TextInput label="Campaign name" placeholder="October announcement" value={name} onChange={(e) => setName(e.currentTarget.value)} required />

              {isPush && (
                <Select
                  label="Target app"
                  description="Which app these device tokens belong to. It selects the APNs topic (bundle id) — a push sent without it is rejected by iOS with DeviceTokenNotForTopic."
                  placeholder="Select the app…"
                  data={catalogue
                    .filter((a) => a.enabled !== false)
                    .map((a) => ({ value: a.appId, label: `${a.displayName || a.shortName || a.appId} · ${a.appId}` }))}
                  value={appId}
                  onChange={setAppId}
                  searchable
                  required
                  nothingFoundMessage="No apps in the catalogue"
                />
              )}

              {isPush && (
                <>
                  <PushImageField value={imageUrl} onChange={setImageUrl} recordId="push-bulk" />
                  <PushSoundField value={sound} onChange={setSound} />
                </>
              )}

              {isWhatsApp ? (
                <WhatsAppTemplateComposer mode="bulk" sampleBodyValues={sampleVars} onChange={setWaOut} />
              ) : (
                <>
                  <Select
                    label="Template (optional)"
                    placeholder={tplLoading ? 'Loading…' : 'Select a template to pre-fill'}
                    data={templates.filter((t) => t.id).map((t) => ({ value: String(t.id), label: String(t.name || t.id) }))}
                    value={templateId}
                    onChange={onSelectTemplate}
                    searchable
                    nothingFoundMessage="No templates"
                  />
                  {showSubject && <TextInput label={channel === 'push' ? 'Title' : 'Subject'} value={subject} onChange={(e) => setSubject(e.currentTarget.value)} />}
                  {isEmail ? (
                    <EmailBodyEditor
                      label="Message (use {{1}}, {{2}}, … for variables)"
                      value={body}
                      onChange={(html) => setBody(html)}
                      minHeight={220}
                    />
                  ) : (
                    <Textarea label="Message (use {{1}}, {{2}}, … for variables)" placeholder="Hi {{1}}, …" minRows={6} autosize value={body} onChange={(e) => setBody(e.currentTarget.value)} required />
                  )}

                  {isEmail && (
                    <Card withBorder padding="sm">
                      <Group justify="space-between" mb={attachments.length > 0 ? 'xs' : 0}>
                        <div>
                          <Text size="sm" fw={500}>Attachments</Text>
                          <Text size="xs" c="dimmed">Sent with every recipient's email. Max 25MB per file.</Text>
                        </div>
                        <FileButton multiple onChange={(files) => files && files.length > 0 && uploadAttachment(files)}>
                          {(props) => <Button {...props} size="xs" variant="light" leftSection={<IconPaperclip size={14} />} loading={attUploading}>Add files</Button>}
                        </FileButton>
                      </Group>
                      {attachments.map((a, i) => (
                        <Group key={a.fileId} justify="space-between" wrap="nowrap" gap="xs" mt={4}>
                          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                            <IconPaperclip size={14} />
                            <Text size="xs" truncate>{a.fileName}</Text>
                          </Group>
                          <ActionIcon size="sm" variant="subtle" color="red" onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}><IconX size={14} /></ActionIcon>
                        </Group>
                      ))}
                    </Card>
                  )}
                </>
              )}

              <Card withBorder padding="sm">
                <Group justify="space-between">
                  <div>
                    <Text size="sm" fw={500}>Recipient file</Text>
                    <Text size="xs" c="dimmed">
                      {requiredVarCount > 0
                        ? `Needs a recipient column + ${requiredVarCount} variable column(s).`
                        : 'Needs a recipient column.'}
                    </Text>
                  </div>
                  <Group gap="xs">
                    <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={() => downloadSample('csv')}>CSV</Button>
                    <Button size="xs" variant="light" leftSection={<IconDownload size={14} />} onClick={() => downloadSample('xlsx')}>Excel</Button>
                  </Group>
                </Group>
              </Card>
            </Stack>
          ),
        },
        {
          title: 'Contacts',
          description: 'Upload your file (or paste), then map the columns.',
          validate: validateContactsStep,
          content: (
            <Stack gap="sm">
              {errAlert}
              <SegmentedControl
                value={mode}
                onChange={(v) => setMode(v as 'upload' | 'paste')}
                data={[{ label: 'Upload CSV/Excel', value: 'upload' }, { label: 'Paste', value: 'paste' }]}
              />
              {mode === 'upload' ? (
                <Group>
                  <FileButton onChange={handleFile} accept=".csv,.xlsx,.xls,text/csv,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">
                    {(props) => <Button {...props} variant="light">Choose CSV / Excel file</Button>}
                  </FileButton>
                  {fileName && <Text size="sm" c="dimmed">{fileName}</Text>}
                  <SegmentedControl size="xs" value={hasHeader ? 'h' : 'n'} onChange={(v) => setHasHeader(v === 'h')}
                    data={[{ label: 'Has header row', value: 'h' }, { label: 'No header', value: 'n' }]} />
                </Group>
              ) : (
                <Textarea
                  label={requiredVarCount === 0 ? `Paste ${recipientLabel}s (separated by commas or new lines)` : 'Paste rows (comma-separated, recipient first, then variables)'}
                  placeholder={requiredVarCount === 0
                    ? (channel === 'email' ? 'jane@example.com, john@example.com' : '+27821234567, +27839876543, +27831112223')
                    : '+27821234567, John\n+27839876543, Mary'}
                  minRows={6}
                  autosize
                  value={pasteText}
                  onChange={(e) => setPasteText(e.currentTarget.value)}
                />
              )}

              {width > 0 && (
                <Card withBorder padding="sm">
                  <Text size="sm" fw={500} mb="xs">Map columns</Text>
                  <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="xs">
                    <Select label="Recipient" data={colOptions} value={String(recipientCol)} onChange={(v) => setRecipientCol(Number(v ?? 0))} />
                    {varCols.map((ci, i) => (
                      <Select
                        key={i}
                        label={`Variable {{${i + 1}}}`}
                        data={colOptions}
                        value={String(ci)}
                        onChange={(v) => setVarCols((prev) => { const next = [...prev]; next[i] = Number(v ?? 0); return next; })}
                      />
                    ))}
                  </SimpleGrid>
                </Card>
              )}

              <Group gap="xs">
                <Badge variant="light" color="green">{valid.length} valid</Badge>
                {dupCount > 0 && <Badge variant="light" color="gray">{dupCount} duplicate{dupCount === 1 ? '' : 's'} removed</Badge>}
                {invalidCount > 0 && <Badge variant="light" color="red">{invalidCount} invalid</Badge>}
                {requiredVarCount > 0 && <Badge variant="light" color="grape">{requiredVarCount} variable(s) mapped</Badge>}
              </Group>

              {invalidCount > 0 && (
                <Alert color="orange" title={`${invalidCount} entry(ies) will be skipped`}>
                  <Text size="xs">These don't look like valid {recipientLabel === 'email' ? 'emails' : recipientLabel === 'device_token' ? 'tokens' : 'numbers'} and won't be sent:</Text>
                  <Text size="xs" ff="monospace" c="dimmed" mt={4} style={{ wordBreak: 'break-all' }}>
                    {invalidSamples.join(', ')}{invalidCount > invalidSamples.length ? ', …' : ''}
                  </Text>
                </Alert>
              )}

              {valid.length > 0 && (
                <Card withBorder padding="xs">
                  <Text size="xs" c="dimmed" mb={4}>Preview (first {Math.min(5, valid.length)} of {valid.length} valid)</Text>
                  <Table withRowBorders={false} verticalSpacing={2} fz="xs">
                    <Table.Tbody>
                      {valid.slice(0, 5).map((r, i) => (
                        <Table.Tr key={i}>
                          <Table.Td>{r.toAddress}</Table.Td>
                          <Table.Td c="dimmed">{(r.variables ?? []).join(' · ')}</Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </Card>
              )}
            </Stack>
          ),
        },
        {
          title: 'Review',
          description: 'Confirm and send.',
          content: (
            <Stack gap="sm">
              <Group justify="space-between"><Text size="sm" c="dimmed">Channel</Text><Badge variant="light">{channel}</Badge></Group>
              <Group justify="space-between"><Text size="sm" c="dimmed">Campaign</Text><Text size="sm">{name || '—'}</Text></Group>
              {isWhatsApp
                ? <Group justify="space-between"><Text size="sm" c="dimmed">Template</Text><Text size="sm">{waOut?.templateName || '—'}</Text></Group>
                : showSubject && <Group justify="space-between"><Text size="sm" c="dimmed">{channel === 'push' ? 'Title' : 'Subject'}</Text><Text size="sm">{subject || '—'}</Text></Group>}
              <Group justify="space-between"><Text size="sm" c="dimmed">Recipients</Text><Badge variant="light" color="blue">{valid.length}{invalidCount > 0 ? ` (${invalidCount} skipped)` : ''}</Badge></Group>
              <Switch
                checked={includeOptedOut}
                onChange={(e) => setIncludeOptedOut(e.currentTarget.checked)}
                label="Send even to opted-out / blocked contacts"
                description="Bypasses our internal opt-out suppression. WhatsApp/Meta still cannot deliver to users who blocked the business number."
              />
              <DateTimePicker
                label="Schedule for later (optional)"
                description="Leave empty to send now. If set, the campaign is sent automatically at this time."
                placeholder="Pick date & time"
                clearable
                minDate={new Date()}
                value={scheduleAt}
                onChange={(v) => setScheduleAt(v as Date | null)}
              />
            </Stack>
          ),
        },
      ]}
    />
  );

  return (
    <>
      <Grid gutter="xl">
        <Grid.Col span={{ base: 12, lg: 8 }}>
          <Card withBorder padding="lg">{wizard}</Card>
        </Grid.Col>
        <Grid.Col span={{ base: 12, lg: 4 }}>
        <Stack gap="xs">
          <Text size="sm" fw={500}>Preview</Text>
          <div style={{ position: 'sticky', top: 16 }}>
            {isWhatsApp ? (
              <WhatsAppMessagePreview template={waOut?.template} mediaUrl={waOut?.mediaUrl} bodyValues={waOut?.bodyValues ?? sampleVars} />
            ) : isEmail ? (
              <Card withBorder padding="md">
                {subject && <Text size="sm" fw={600} mb={6}>{subst(subject, sampleVars)}</Text>}
                <HtmlEmailPreview html={subst(body, sampleVars)} height={420} />
              </Card>
            ) : (
              <Card withBorder padding="md">
                {showSubject && subject && <Text size="sm" fw={600} mb={4}>{subst(subject, sampleVars)}</Text>}
                <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>
                  {subst(body, sampleVars) || <Text span c="dimmed">Your message preview will appear here…</Text>}
                </Text>
              </Card>
            )}
          </div>
        </Stack>
        </Grid.Col>
      </Grid>

      <Modal opened={result !== null} onClose={reset} title={result?.statusId === 1 ? 'Campaign scheduled' : 'Campaign sent'} centered>
        <Stack gap="md" align="center">
          <IconCircleCheck size={56} color="var(--mantine-color-green-6)" />
          <Title order={4}>{result?.statusId === 1 ? 'Campaign scheduled' : 'Campaign sent'}</Title>
          {result?.statusId === 1 ? (
            <>
              <Badge size="lg" color="blue" variant="light">{result?.totalRecipients ?? 0} recipient(s)</Badge>
              <Text size="sm" c="dimmed" ta="center">
                "{result?.name}" is scheduled{scheduleAt ? ` for ${scheduleAt.toLocaleString()}` : ''} on the {channel} channel. It will send automatically and appear on the Campaigns page.
              </Text>
            </>
          ) : (
            <>
              <Group gap="sm">
                <Badge size="lg" color="blue" variant="light">{result?.sentCount ?? 0} sent/queued</Badge>
                {(result?.failedCount ?? 0) > 0 && <Badge size="lg" color="red" variant="light">{result?.failedCount} failed</Badge>}
                <Badge size="lg" color="gray" variant="light">{result?.totalRecipients ?? 0} total</Badge>
              </Group>
              <Text size="sm" c="dimmed" ta="center">"{result?.name}" was sent on the {channel} channel. Track delivery on the Campaigns page.</Text>
            </>
          )}
          <Group>
            <Button variant="light" onClick={reset}>Create another</Button>
            <Button onClick={reset}>Done</Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}
